/**
 * Every sentence of the ways out on the ad's page and of Media
 * (docs/SPEC.md §13, §13.2, §14.3, §15, §30.5; P24-T05): Approve and the
 * three cards, the outputs under the player, a true render of a moment, and
 * the folders by ad. The true render's sentences are Phase 13's
 * (`live/copy.ts`), copied: `live/` goes with P25.
 */
import { ApiError } from "../api/client";
import type { Output } from "../api/outputs";
import { fillAll } from "./changes";
import { sentenceFor } from "./errors";

export const say = (s: string, p: Record<string, unknown> = {}) =>
  fillAll(s, p) ?? s;

export const WAYS_OUT = {
  approve: "Approve",
  approveHint: "Choose how the film leaves TrueReel.",
  title: "The ways out",
  intro:
    "Each is made from version {n}, as it plays now. You can take all three, and the film stays editable.",
  close: "Close",
  chosen: "Chosen",
  credits: "{credits} credits",
  free: "Free",
  resolution: "Resolution",
  language: "Language",
  pricesFailed: "The prices could not be read. Try again in a moment.",
  retry: "Try again",
  started:
    "Started. Its progress is under the player, and the file stays in Media.",
  rt: {
    title: "The real-time film",
    body: "The player's own engine renders the film on our servers at the resolution you choose. The quickest and the cheapest.",
    time: "Seconds to a minute",
    make: "Make the real-time film · {credits} credits",
    /** On a CPU node the real-time film is free (§15.1). */
    makeFree: "Make the real-time film · Free",
  },
  final: {
    title: "The ray-traced final",
    body: "Every frame ray-traced on the render farm from the same description, at {resolution}p.",
    time: "About {minutes} min of rendering",
    make: "Make the ray-traced final · {credits} credits",
    moment: "See one moment first",
    momentHint: "A true render of the paused moment: {credits} credits.",
  },
  elsewhere: {
    title: "A generative film elsewhere",
    body: "Take the detailed scenario, prompts and reference pictures to another video-generation service.",
    time: "Free · pictures drawn in your browser",
    package: "Download generation package (ZIP)",
    packaging: "Preparing prompts and reference pictures…",
    packageHint:
      "Detailed scene prompts, original artwork, start/middle/end reference pictures and instructions for Flow or another video service. Nothing is sent to them automatically.",
    includeRequested:
      "Include my latest reviewed, unfinished changes as direction (the preview may not show them).",
    cancelPackage: "Cancel download",
    packageCancelled: "Download cancelled.",
    packageNoRequested:
      "Package downloaded from the current film. There were no reviewed unfinished changes to include.",
    packageDone: "Package downloaded. Unzip it and start with README.md.",
    packageMissing:
      "Package downloaded. {n} reference pictures could not be drawn; PACKAGE.json lists them. The prompts and artwork are included.",
    packageTooLarge:
      "The reference files are too large to package together. Use smaller pictures and try again.",
    packageFailed:
      "The package could not be made. Check that the film and its pictures are ready, then try again.",
    copy: "Copy the scene's description",
    copying: "Copying…",
    zip: "Download the scenes' pictures (zip)",
    zipping: "Drawing the pictures…",
    zipMissing:
      "{n} scenes could not be drawn and are not in the zip. Try again in a moment.",
    zipFailed: "The pictures could not be drawn. Try again in a moment.",
    manual:
      "The text could not be put on the clipboard. Select it here and copy it.",
    failed: "The description could not be made. Try again in a moment.",
    textLabel: "The scene's description",
    refine: "Refine with {provider}",
    refineHint:
      "{provider} redraws the film from its frames, its depth and its objects. Not open yet: nothing is sent and nothing is charged.",
  },
} as const;

export const OUTPUTS = {
  title: "Made from this ad",
  names: {
    rt: "Real-time film",
    final: "Ray-traced final",
    still: "True render",
  } as Record<string, string>,
  line: "{name} · {aspect} · {resolution}p · {language}",
  queued: "Waiting for a machine…",
  working: "Rendering… {percent} %",
  done: "Ready",
  cancelled: "Stopped. Only what was made is charged.",
  stop: "Stop",
  download: "Download",
  frameAlt: "The last frame rendered of {name}",
  credits: "{credits} credits",
  seconds: "{seconds} s",
  inMedia: "Every file is also in Media.",
} as const;

export const TRUE_RENDER = {
  title: "True render",
  button: "True render of this moment",
  pauseFirst: "Pause at the moment you want first.",
  hint: "One frame ray-traced at the final's options: scene {n} (“{name}”) at {time} s.",
  cost: "About {credits} credits ({seconds} seconds of rendering).",
  filmCamera:
    "A true render shows the film's own camera, not the view you moved to.",
  confirm: "Render this moment · {credits} credits",
  cancel: "Cancel",
  reused: "You already have this picture.",
  pending: "Waiting for a render machine…",
  rendering: "Rendering…",
  stop: "Stop",
  label: "Scene {n} at {time} s",
  alt: "True render of scene {n} at {time} s",
  goto: "Go to this moment",
  download: "Download",
  failed: "The true render could not be made. Nothing was charged.",
  cancelled: "Stopped. Nothing was charged.",
  waitWarm: "A render machine is running, so this should start soon.",
  waitWarmSeconds:
    "A render machine is running. Recent true renders started after about {seconds} seconds.",
  waitWarmMinutes:
    "A render machine is running. Recent true renders started after about {minutes} minutes.",
  waitCold:
    "A render machine has to start first, which takes minutes. You can leave this page; the picture will be here when you come back.",
  waitColdMinutes:
    "A render machine has to start first. Recent true renders waited about {minutes} minutes for one. You can leave this page; the picture will be here when you come back.",
  waitBusy:
    "The render machines are busy with other renders; this one starts when one is free. You can leave this page; the picture will be here when you come back.",
  waitUnavailable:
    "Render machines cannot be started right now. The picture stays in the queue and is made as soon as one can be; nothing is charged while it waits, and you can stop it.",
} as const;

export const MEDIA = {
  eyebrow: "— Media",
  title: "Media",
  meta: "Your ads' films, review files and uploaded images, in a folder per ad. An uploaded logo or product shot can go into any ad and any brand kit.",
  upload: "Upload image",
  uploads: "Uploads",
  files: "{count} files",
  file: "1 file",
  openAd: "Open ad",
  deleteAd: "Delete ad",
  deleteAdTitle: "Deletes the ad and all of its files",
  deleting: "Deleting…",
  delete: "Delete",
  deleteFile: "Delete {name}",
  videoPreview: "Preview {name}",
  download: "Download",
  confirmAd:
    "Delete this ad? Its films and true renders leave your media and the space is freed at once. The images uploaded for it stay in Uploads.",
  confirmFile: "Delete {name}? The space is freed at once.",
  emptyTitle: "Nothing stored yet.",
  emptyBody:
    "The films you make and the images you upload appear here, in a folder per ad, and count against your quota. Nothing is deleted unless you delete it.",
  emptyUpload: "Upload an image",
  makeAd: "Make an ad",
  kinds: {
    rt: "real-time",
    final: "final",
    still: "true render",
    upload: "upload",
  } as Record<string, string>,
  uploadsEmpty: "Images you upload that belong to no ad appear here.",
} as const;

/** Review links (§14.5): the panel under the film. */
export const SHARE = {
  title: "Share for review",
  meta: "Anyone with the link can watch the film and leave comments pinned to a moment; no account and no download.",
  days: "Expires in (days)",
  comments: "Allow comments",
  reuse: "Allow reuse",
  reuseHint:
    "Another organisation that has the link may use this film's scenes in its own films and copy the templates made from it.",
  create: "Create review link",
  creating: "Creating…",
  ready:
    "Your review link is ready. It will not be shown again, so copy it now.",
  linkLabel: "Review link",
  copy: "Copy",
  copied: "Copied",
  done: "Done",
  copyFailed:
    "Copying is not available here; select the link and copy it yourself.",
  links: "Review links",
  none: "No review links yet.",
  status: { active: "active", revoked: "revoked", expired: "expired" },
  expires: "expires",
  expired: "expired",
  commentsOn: "comments on",
  commentsOff: "comments off",
  reuseOn: "reuse allowed",
  revoke: "Revoke",
  revoking: "Revoking…",
  commentsTitle: "Comments",
  hide: "Hide",
  hiding: "Hiding…",
} as const;

/** An output's one line: "Real-time film · 9:16 · 1080p · en". */
export function outputLine(
  o: Pick<Output, "kind" | "aspect" | "resolution" | "language">,
): string {
  return say(OUTPUTS.line, {
    name: OUTPUTS.names[o.kind] ?? o.kind,
    aspect: o.aspect,
    resolution: String(o.resolution),
    language: o.language,
  });
}

/** A route's error on the ways out, as a sentence (§1 rule 7). */
export function outputError(error: unknown): string {
  if (error instanceof ApiError && error.code === "no_version") {
    return "The film has no version that played yet.";
  }
  return sentenceFor(error);
}

/** Reuse of a film shared with Allow reuse (§11.2 (3), §12, §14.5; P27-T01):
 *  the panel of the review page for a signed-in person. */
export const REUSE = {
  title: "Use this film",
  meta: "Whoever shared this film allows its scenes and templates to be used in your own films. What you take is a copy: it is yours, and nothing you do changes theirs.",
  signIn: "Sign in to TrueReel to use this film's scenes and templates.",
  signInLink: "Sign in",
  scenes: "Scenes",
  into: "Into my ad",
  noAds: "You have no film yet to add a scene to.",
  makeAd: "Make an ad",
  use: "Use in my ad",
  using: "Adding…",
  templates: "Templates",
  copy: "Copy to my templates",
  copying: "Copying…",
  copied: "Copied to your templates.",
  makeFrom: "Make an ad from it",
  seconds: (s: number) => `${Math.round(s * 10) / 10} s`,
  failed: {
    reuse_objects:
      "This template uses objects that only its owner can use, so it cannot be copied.",
    change_pending:
      "That ad is being changed right now. Wait for the change to finish, then try again.",
    no_version: "That ad has no film yet. Make its film first.",
    rate_limited:
      "Too many changes in a short time. Please wait a moment and try again.",
    not_found: "This link no longer allows reuse.",
  } as Record<string, string>,
  failedOther: "That did not work. Please try again.",
} as const;
